import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfig } from '@mkp/shared/util-app-config';
import { inviteApiActions } from '@mkp/account-membership/actions';
import { InviteResource } from '@mkp/account-membership/data-access';
import { selectWorkspaceFromUrlStatus, WorkspaceFromUrlStatus } from '@mkp/auth/state';
import { SnackbarService } from '@mkp/shared/util-snackbar';
import { isHttpErrorResponse } from '@mkp/shared/util-format';
import {
  accountAggregateApiActions,
  redeemInviteActions,
} from '@mkp/user-account-membership/actions';
import { AccountAggregateService } from '@mkp/user-account-membership/feature-account-aggregate';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { userApiActions } from '@mkp/user/state/actions';
import { selectLoggedInUserId } from '@mkp/user/state-logged-in/selectors';
import { map, of } from 'rxjs';
import { catchError, exhaustMap, filter, switchMap, tap } from 'rxjs/operators';
import {
  extractEmailFromError,
  extractEmailsFromParameters,
} from './helpers/redeem-invite.helpers';

const { routes } = AppConfig;

export const redeemInvite = createEffect(
  (actions$ = inject(Actions), inviteResource = inject(InviteResource), store = inject(Store)) => {
    return actions$.pipe(
      ofType(redeemInviteActions.opened),
      switchMap(({ token }) =>
        store.select(selectWorkspaceFromUrlStatus).pipe(
          filter((status) =>
            [WorkspaceFromUrlStatus.COMPLETED, WorkspaceFromUrlStatus.LOGGED_OUT].includes(status)
          ),
          map(() => token)
        )
      ),
      filter(Boolean),
      exhaustMap((token) =>
        inviteResource.redeemInvite(token).pipe(
          map(() => inviteApiActions.redeemInviteSuccess()),
          catchError((error: unknown) => {
            const email = extractEmailFromError(error);

            const { actorEmail, invitedEmail } = extractEmailsFromParameters(error);

            // Check if it's an email mismatch error (has both emails)
            if (actorEmail && invitedEmail && actorEmail !== invitedEmail) {
              return of(inviteApiActions.redeemInviteEmailMismatch({ actorEmail, invitedEmail }));
            }

            // Check if it's a user non-existing error
            const action = isUserNonExistingError(error, email)
              ? inviteApiActions.redeemInviteForNonExistingUser({ token, email })
              : inviteApiActions.redeemInviteFailure({ error });
            return of(action);
          })
        )
      )
    );
  },
  { functional: true }
);

export const refreshAccountAggregateOnRedeemInviteSuccess = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    accountAggregateService = inject(AccountAggregateService)
  ) => {
    return actions$.pipe(
      ofType(inviteApiActions.redeemInviteSuccess),
      concatLatestFrom(() => store.select(selectLoggedInUserId)),
      exhaustMap(([, loggedInUserId]) =>
        loggedInUserId
          ? accountAggregateService.refreshAccountAggregate(loggedInUserId).pipe(
              map(accountAggregateApiActions.refreshOnRedeemInviteSuccess),
              catchError((error: unknown) =>
                of(accountAggregateApiActions.refreshOnRedeemInviteFailure({ error }))
              )
            )
          : of(inviteApiActions.redeemInviteLoggedOutSuccess())
      )
    );
  },
  { functional: true }
);

export const redeemInviteAfterUserCreation = createEffect(
  (actions$ = inject(Actions), inviteResource = inject(InviteResource)) => {
    return actions$.pipe(
      ofType(userApiActions.createPasswordlessUserSuccess),
      exhaustMap(({ token, email }) =>
        inviteResource.redeemInvite(token).pipe(
          map(() => inviteApiActions.redeemInviteAfterUserCreationSuccess({ email })),
          catchError((error: unknown) =>
            of(
              inviteApiActions.redeemInviteAfterUserCreationFailure({
                error,
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const redeemInviteFailure = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(
        inviteApiActions.redeemInviteFailure,
        inviteApiActions.redeemInviteAfterUserCreationFailure
      ),
      tap(() => router.navigate([routes.invalidInvite]))
    );
  },
  { functional: true, dispatch: false }
);

export const redeemInviteEmailMismatch = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(inviteApiActions.redeemInviteEmailMismatch),
      tap(({ actorEmail, invitedEmail }) => {
        router.navigate([routes.invalidInvite], {
          state: {
            actorEmail,
            invitedEmail,
            redeemInviteUrl: router.url,
          },
        });
      })
    );
  },
  { functional: true, dispatch: false }
);

export const redirectOnRedeemSuccess = createEffect(
  (
    actions$ = inject(Actions),
    router = inject(Router),
    snackbarService = inject(SnackbarService)
  ) => {
    return actions$.pipe(
      ofType(
        accountAggregateApiActions.refreshOnRedeemInviteSuccess,
        inviteApiActions.redeemInviteLoggedOutSuccess
      ),
      tap(() => router.navigate([routes.dashboard])),
      tap(() => snackbarService.show('USER_MANAGEMENT.INVITE.SNACKBAR.REDEEM.SUCCESS'))
    );
  },
  { functional: true, dispatch: false }
);

const isUserNonExistingError = (error: unknown, email: string) =>
  isHttpErrorResponse(error) && error.status === 400 && email;
